/**
 * analytics.ts — the public-site-only PostHog client (docs/02c-analytics-spec.md
 * §7, ADR 015). A separate bundle from `public.ts` (not a shared module — esbuild
 * gives each `src/client/*.ts` entry its own IIFE scope) because `base.html` is
 * the ONLY layout that loads it: `office-shell.html`/`office-auth.html` never
 * extend `base.html` and never render this script tag, which is what actually
 * keeps the SDK out of `/office/*` and `/portal` — not a runtime check.
 *
 * Exposes `window.emitEvent(name, properties)` so `public.ts` (and any later
 * client bundle) can fire a custom event without importing posthog-js directly —
 * if the vendor ever changes, only this file's internals do.
 *
 * `sanitizeProperties` is exported as a standalone function (not an inline arrow
 * inside `init()`'s options) specifically so `tests/unit/analytics-client.test.ts`
 * can import and assert it without needing a browser/jsdom environment — the rest
 * of this file only runs inside `initAnalytics()`, guarded on `window` existing.
 */
import type { PostHog as PostHogClient } from "posthog-js";

declare global {
  interface Window {
    __ANALYTICS__?: { projectKey: string };
    emitEvent?: (name: string, properties?: Record<string, unknown>) => void;
    getAnalyticsDistinctId?: () => string | null;
  }
}

/**
 * ADR 015 §2 — assign, never delete. posthog-js never puts `$ip`/`$geoip_*` into
 * the outgoing payload, so deleting either is a no-op that does the opposite of
 * what it looks like: deleting `$ip` is the documented way to tell ingestion to
 * *fall back to* the connection IP. An explicit `null`/`true` IS honoured at
 * ingestion. This is belt-and-suspenders on top of the `/ph/*` proxy (the
 * primary control — the visitor's IP never reaches PostHog's transport layer at
 * all), never the only mechanism by itself.
 */
export function sanitizeProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  properties.$ip = null;
  properties.$geoip_disable = true;
  return properties;
}

async function initAnalytics(): Promise<void> {
  const config = window.__ANALYTICS__;
  if (!config?.projectKey) return;

  const { default: posthog } = await import("posthog-js");

  posthog.init(config.projectKey, {
    api_host: "/ph",
    ui_host: "https://eu.posthog.com",
    autocapture: false,
    capture_pageview: true,
    person_profiles: "identified_only",
    persistence: "sessionStorage",
    sanitize_properties: sanitizeProperties,
    // `loaded` runs synchronously before `capture_pageview`'s automatic first
    // capture — calling `register()` here (rather than right after `init()`
    // returns) is what makes that first $pageview carry `language` too, not
    // just every event after it. design.md §3 intro: language rides on every
    // event this way; a locale switch is a full navigation (the lang-toggle is
    // a plain <a href>, never client-side routing), so there is no separate
    // "toggle" to hook — each page's own init call registers the value fresh.
    loaded: (ph: PostHogClient) => {
      ph.register({ language: document.documentElement.lang });
    },
  });

  window.emitEvent = (name, properties = {}) => {
    posthog.capture(name, properties);
  };
  // ADR 014 §"identity continuity" — S4/S6's submit body carries this so the
  // server-side …_submitted capture reconciles to the same anonymous person
  // instead of Funnel A/B showing a false 0% conversion.
  window.getAnalyticsDistinctId = () => posthog.get_distinct_id();
}

if (typeof window !== "undefined") {
  void initAnalytics();
}
